import {
    AdapterRegistry,
    isLogicAppExpression,
    ITridentResource,
    LibraryVariable,
    Metamodel,
    ModelConstants,
    NamedAsyncAdapter,
    Owned,
    SnowflakeV2Sink,
    SnowflakeV2Source,
    stringIsNullOrEmpty,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import { CopyJob, NEW_COPY_JOB_ACTIVITY_NAME } from 'app/model/copyjob/copy-job';
import { Model } from 'app/model/backendmodel';
import {
    connectionSupportEnumerateDatabase,
    isWorkspaceBuiltInType
} from 'app/model/adapters/linkedservices/linked-service-adapter.util';
import moment from 'moment-timezone';
import { cloneDeep, has, head, isArray, isObject, isString, map, merge, omit, pick, uniq } from 'lodash';
import {
    ChangeReadStrategy,
    CopyJobActivity,
    NullWatermarkBehavior,
    ReadMethod
} from 'app/model/copyjob/copy-job.activity';
import { CopyJobMode } from 'app/model/copyjob/type';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import {
    convertToModelAsync,
    convertToResourceAsync,
    getModelByLinkedServiceType,
    getReferencedModelByDatasetType
} from 'app/shared/utils/model-converter.util';
import {
    convertSQLWriteBehaviorToCopyJobWriteBehavior,
    convertTableActionOptionToWriteBehavior,
    isFileType,
    isFileTypeDataset,
    updateMethodSupportCDC
} from 'app/authoring/copy-job/wizard/copy-job.util';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { IFrameService } from 'app/services/iframe.service';
import { getLinkedServiceTypeFromDatasetMetadataAndConnectionKind } from 'app/shared/utils/linkedservice-type.util';
import { adfModelTypeMap, resolveLinkedServiceType } from 'app/registries/consts/adf-model-type-map-const';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { SqlSink } from 'app/model/pipeline/copysink/sql.copysink';
import { LakehouseTableCopySink } from 'app/model/pipeline/copysink/lakehouse-table.copysink';
import {
    getCopySinkModelFromDatasetType,
    getCopySourceModelFromDatasetAndLinkedServiceType,
    isAncestor
} from 'app/authoring/component/activities/copy-activity/util';
import { StoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/store-read-settings';
import { FormatReadSettings } from 'app/model/pipeline/copysource/format-read-settings/format-read-settings';
import { StoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/store-write-settings';
import { FormatWriteSettings } from 'app/model/pipeline/copysink/format-write-settings/format-write-settings';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { GlobalVariables } from 'app/services/globals';
import { isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';
import { parseKeyValueStringToObject } from 'app/shared/utils/string.util';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { CopyJobActivityContext, CopyJobDefinitionContext } from 'app/interfaces/copilot.copyjob.type';
import { copyMethodOptionValues } from 'app/shared/components/copy-method-setting/copy-method-setting.const';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { OracleFusionCopySource } from 'app/model/pipeline/copysource/oracle-fusion.copysource';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(CopyJob),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            jobMode: '*',
            fullLoadBehavior: '*',
            useSharedCompute: GlobalVariables.copyJobUseSharedCompute ? '*' : undefined,
            dataFactoryId: '*',
            source: {
                type: '*',
                connectionSettings: {
                    type: '*',
                    typeProperties: '*',
                    linkedServiceName: '*',
                    externalReferences: '*'
                },
                additionalSettings: {
                    storageIntegration: '*',
                    dbNameForStagingStreams: GlobalVariables.snowflakeCDCSharedDbCopyJob ? '*' : undefined,
                    schemaNameForStagingStreams: GlobalVariables.snowflakeCDCSharedDbCopyJob ? '*' : undefined,
                    protocolSettings: {
                        requestTimeout: '*'
                    }
                },
                genericChangeDataProperties: GlobalVariables.copyJobGenericCdc ? '*' : undefined
            },
            destination: {
                type: '*',
                connectionSettings: {
                    type: '*',
                    typeProperties: '*',
                    linkedServiceName: '*',
                    externalReferences: '*'
                },
                additionalSettings: {
                    storageIntegration: '*',
                    protocolSettings: {
                        requestMethod: '*'
                    }
                }
            },
            defaultActivityProperties: {
				destination: {
					schemaNameForStagingTables: GlobalVariables.copyJobCustomStagingSchema ? '*' : undefined
				},
			},
            stagingSettings: '*',
            policy: {
                timeout: '*',
                retry: '*'
            }
        },
        activities: '*',
        libraryVariables: '*'
    }
})
export class CopyJobAdapter extends NamedAsyncAdapter<CopyJob, Model.CopyJobResource> {
    public override async toResource(named: CopyJob, ...other: unknown[]): Promise<Model.CopyJobResource> {
        const resource = await super.toResource(named, ...other);
        if (resource.libraryVariables != null) {
            const clonedResource = cloneDeep(resource);
            const libraryVariables = clonedResource.libraryVariables;
            Reflect.deleteProperty(clonedResource, 'libraryVariables');
            try {
                this._libraryVariablesPostToResource(clonedResource, libraryVariables);
                return clonedResource;
            } catch (e) {
                this._logError('Failed to convert library variables in library variables post to resource', e);
            }
        }
        return resource;
    }

    public override async toModel(resource: Model.CopyJobResource, ...other: unknown[]): Promise<CopyJob> {
        const clonedResource = cloneDeep(resource);

        try {
            // Check for duplicate activity IDs
            if (clonedResource?.activities != null && isArray(clonedResource.activities)) {
                const activityIds = new Set<string>();
                const duplicateIds: string[] = [];

                clonedResource.activities.forEach((activity) => {
                    if (!stringIsNullOrEmpty(activity.id)) {
                        if (activityIds.has(activity.id)) {
                            duplicateIds.push(activity.id);
                        } else {
                            activityIds.add(activity.id);
                        }
                    }
                });

                if (duplicateIds.length > 0) {
                    this._logError(`Duplicate activity IDs found in copy job`, {
                        objectId: clonedResource.objectId,
                        duplicateIds: `${uniq(duplicateIds).join(', ')}`,
                        duplicateCount: String(duplicateIds.length),
                        activityCount: String(clonedResource.activities.length)
                    });
                }
            }
        } catch {
            // ignored
        }

        let copyJob: CopyJob;
        try {
            this._libraryVariablesPreToModel(clonedResource, true, clonedResource);
            copyJob = await super.toModel(clonedResource, ...other);
        } catch (e) {
            this._logError('Failed to convert library variables in library variables pre to model', e);
            copyJob = await super.toModel(resource, ...other);
        }

        // Detect clone delta table: Binary source + Lakehouse connection + Tables rootFolder
        if (
            GlobalVariables.cloneLHTable &&
            resource.properties?.source?.type === adfModelTypeMap.binary.datasetType &&
            resource.properties?.source?.connectionSettings?.type === adfModelTypeMap.lakehouse.linkedServiceType &&
            resource.properties?.source?.connectionSettings?.typeProperties?.rootFolder === 'Tables'
        ) {
            copyJob.isCloneDeltaTable = true;
        }

        return copyJob;
    }

    /**
     * convert copy job resource (Fabric) to copy activity resource (ADF), used in to model
     * @param copyJob
     */
    public async convertCopyJobResourceToCopyActivityResource(
        copyJob: Model.CopyJobResource
    ): Promise<Model.CopyJobActivity[]> {
        // region: Convert table to table name
        const sourceDatasetType = copyJob.properties.source.type;
        const destinationDatasetType = copyJob.properties.destination.type;
        const datasetRegistryService = await getServiceInstance<DatasetRegistryService>(
            this.context.adapterConfig.contextData,
            'DatasetRegistryService'
        );
        const sourceDatasetMetadata = datasetRegistryService.getDatasetConfiguration(sourceDatasetType);
        const destinationDatasetMetadata = datasetRegistryService.getDatasetConfiguration(destinationDatasetType);
        const sourceLinkedServiceType = copyJob.properties.source.connectionSettings.type;
        const destinationLinkedServiceType = resolveLinkedServiceType(
            copyJob.properties.destination.connectionSettings.type
        );
        const connectionPropertiesSource = copyJob.properties.source.connectionSettings.connectionProperties;
        const connectionPropertiesDest = copyJob.properties.destination.connectionSettings.connectionProperties;
        const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
            this.context.adapterConfig.contextData,
            'LinkedServiceRegistryService'
        );
        const sourceLinkedServiceMetadata =
            linkedServiceRegistryService.getLinkedServiceConfiguration(sourceLinkedServiceType);
        const destinationLinkedServiceMetadata =
            linkedServiceRegistryService.getLinkedServiceConfiguration(destinationLinkedServiceType);
        copyJob.activities.forEach((activity) => {
            if (has(activity.properties.source.datasetSettings, 'table') && !sourceDatasetMetadata.splitTablePath) {
                const tableNameAlias = sourceLinkedServiceMetadata.tableNameAlias ?? 'tableName';
                if (tableNameAlias !== 'table') {
                    Reflect.set(
                        activity.properties.source.datasetSettings,
                        tableNameAlias,
                        activity.properties.source.datasetSettings.table
                    );
                    Reflect.deleteProperty(activity.properties.source.datasetSettings, 'table');
                }
            }
            if (
                has(activity.properties.destination.datasetSettings, 'table') &&
                !destinationDatasetMetadata.splitTablePath
            ) {
                const tableNameAlias = destinationLinkedServiceMetadata.tableNameAlias ?? 'tableName';
                if (tableNameAlias !== 'table') {
                    Reflect.set(
                        activity.properties.destination.datasetSettings,
                        tableNameAlias,
                        activity.properties.destination.datasetSettings.table
                    );
                    Reflect.deleteProperty(activity.properties.destination.datasetSettings, 'table');
                }
            }
            if (connectionPropertiesSource && activity.properties.source?.connectionProperties) {
                Reflect.deleteProperty(activity.properties.source, 'connectionProperties');
            }
            if (connectionPropertiesDest && activity.properties.destination?.connectionProperties) {
                Reflect.deleteProperty(activity.properties.destination, 'connectionProperties');
            }
        });
        // endregion

        const copySourceType = getCopySourceModelFromDatasetAndLinkedServiceType(
            sourceDatasetType,
            sourceLinkedServiceMetadata
        ).name;
        const copySinkModel = getCopySinkModelFromDatasetType(destinationDatasetType);
        const copySinkType = copySinkModel.name;
        const tableAlias = sourceDatasetMetadata.splitTablePath
            ? sourceDatasetMetadata.tableAlias || 'table'
            : sourceDatasetMetadata.tableNameAlias || 'tableName';
        const queryAlias = sourceLinkedServiceMetadata.queryAlias || sourceDatasetMetadata.queryAlias || 'query';
        return copyJob.activities.map((activity) => {
            const translator = activity.properties.translator;
            if (activity.properties.typeConversionSettings != null) {
                translator.typeConversion = true;
                translator.typeConversionSettings = activity.properties.typeConversionSettings.typeConversion;
            }
            if (activity.properties.columnFlattenSettings != null) {
                translator.columnFlattenSettings = {
                    treatArrayAsString: !activity.properties.columnFlattenSettings.expandArrayIntoMultipleRows,
                    treatStructAsString: !activity.properties.columnFlattenSettings.flattenNestedProperties,
                    flattenColumnDelimiter: activity.properties.columnFlattenSettings.flattenColumnDelimiter
                };
            }
            if (isArray(translator?.mappings)) {
                translator.mappings = translator.mappings.map((mapping) => ({
                    source: mapping?.source,
                    sink: mapping?.destination
                }));
            }
            const resource: Model.CopyJobActivity = {
                name: activity.id,
                type: ModelConstants.copyJobActivity,
                typeProperties: {
                    source: {
                        type: copySourceType,
                        datasetSettings: {
                            type: sourceDatasetType,
                            schema: [],
                            typeProperties: activity.properties.source.datasetSettings
                        },
                        additionalColumns: activity.properties.auditColumns,
                        ...omit(
                            activity.properties.source,
                            'datasetSettings',
                            'changeDataSettings',
                            'partitionSettings'
                        )
                    },
                    sink: {
                        type: copySinkType,
                        datasetSettings: {
                            type: destinationDatasetType,
                            schema: [],
                            typeProperties: activity.properties.destination.datasetSettings
                        },
                        ...omit(activity.properties.destination, 'datasetSettings')
                    },
                    enableStaging: activity.properties.enableStaging,
                    stagingSettings: activity.properties.stagingSettings,
                    translator,
                    preserve: activity.properties.preserve
                }
            };

            if (isWorkspaceBuiltInType(sourceLinkedServiceType)) {
                resource.typeProperties.source.datasetSettings.linkedService = {
                    properties: copyJob.properties.source.connectionSettings
                };
            } else if (
                !stringIsNullOrEmpty(copyJob.properties.source.connectionSettings?.typeProperties?.containerUri)
            ) {
                resource.typeProperties.source.datasetSettings.linkedService = {
                    name: 'sampleDatasetItemName',
                    properties: {
                        ...copyJob.properties.source.connectionSettings,
                        tridentArtifactType: 'SampleDataset'
                    }
                };
                resource.typeProperties.source.datasetSettings.linkedService.properties.typeProperties.authenticationType =
                    'Anonymous';
            } else {
                resource.typeProperties.source.datasetSettings.linkedServiceName =
                    copyJob.properties.source.connectionSettings.linkedServiceName;

                // Apply the database to the source dataset settings
                resource.typeProperties.source.datasetSettings.typeProperties = {
                    ...copyJob.properties.source.connectionSettings?.typeProperties,
                    ...resource.typeProperties.source.datasetSettings.typeProperties
                };
            }

            if (isWorkspaceBuiltInType(destinationLinkedServiceType)) {
                resource.typeProperties.sink.datasetSettings.linkedService = {
                    properties: {
                        ...copyJob.properties.destination.connectionSettings,
                        type: destinationLinkedServiceType
                    }
                };
            } else {
                resource.typeProperties.sink.datasetSettings.linkedServiceName =
                    copyJob.properties.destination.connectionSettings.linkedServiceName;

                // Apply the database to the sink dataset settings
                resource.typeProperties.sink.datasetSettings.typeProperties = {
                    ...copyJob.properties.destination.connectionSettings?.typeProperties,
                    ...resource.typeProperties.sink.datasetSettings.typeProperties
                };
            }

            if (activity.properties.source.changeDataSettings != null) {
                resource.typeProperties.readMethod = activity.properties.source.changeDataSettings.readMethod;
                resource.typeProperties.columns = activity.properties.source.changeDataSettings.columns;
                if (GlobalVariables.copyJobCustomCaptureInstance) {
                    resource.typeProperties.captureInstanceName =
                        activity.properties.source.changeDataSettings.captureInstanceName;
                }
                if (GlobalVariables.copyJobNullRecords) {
                    resource.typeProperties.nullWatermarkBehavior =
                        activity.properties.source.changeDataSettings.nullWatermarkBehavior;
                }
            }

            if (
                GlobalVariables.copyJobRefreshWithoutIncrementalColumn &&
                activity.properties.source.changeReadStrategy != null
            ) {
                resource.typeProperties.changeReadStrategy = activity.properties.source.changeReadStrategy;
            }

            if (activity.properties.source.partitionSettings != null) {
                const partitionOption = activity.properties.source.partitionSettings.partitionOption;
                resource.typeProperties.source.partitionOption =
                    partitionOption === 'Auto' ? partitionOptionConst.autoDetect : partitionOption;
            }

            // region setup source store settings type and format settings type
            if (isFileType(resource.typeProperties.source.datasetSettings)) {
                const storeSettingsModel = getModelByLinkedServiceType(
                    sourceLinkedServiceType,
                    StoreReadSettings.MODEL
                );
                if (resource.typeProperties.source.storeSettings != null) {
                    resource.typeProperties.source.storeSettings.type = storeSettingsModel?.name;
                    if (resource.typeProperties.source.storeSettings.type === 'HttpReadSettings') {
                        const additionalHeaders = (
                            activity.properties.source.storeSettings as {
                                additionalHeaders?: Record<string, string>;
                            }
                        ).additionalHeaders;
                        if (additionalHeaders != null) {
                            (
                                resource.typeProperties.source.storeSettings as Model.HttpStoreReadSettings
                            ).additionalHeaders = map(additionalHeaders, (value, key) => `${key}: ${value}`).join(
                                ',\n'
                            );
                        }
                    }
                } else if (storeSettingsModel != null) {
                    resource.typeProperties.source.storeSettings = {
                        type: storeSettingsModel.name
                    };
                }

                const formatSettingsModel = getReferencedModelByDatasetType(
                    sourceDatasetType,
                    FormatReadSettings.MODEL
                );
                if (resource.typeProperties.source.formatSettings != null) {
                    resource.typeProperties.source.formatSettings.type = formatSettingsModel?.name;
                } else if (formatSettingsModel != null) {
                    resource.typeProperties.source.formatSettings = {
                        type: formatSettingsModel.name
                    };
                }

                this._processFileFormatSettingsToModel(activity, resource, sourceDatasetType, 'source');
            }
            // endregion

            // region setup sink store settings type and format settings type
            if (isFileType(resource.typeProperties.sink.datasetSettings)) {
                const storeSettingsModel = getModelByLinkedServiceType(
                    destinationLinkedServiceType,
                    StoreWriteSettings.MODEL
                );
                if (resource.typeProperties.sink.storeSettings != null) {
                    resource.typeProperties.sink.storeSettings.type = storeSettingsModel?.name;
                } else if (storeSettingsModel != null) {
                    resource.typeProperties.sink.storeSettings = {
                        type: storeSettingsModel.name
                    };
                }

                const formatSettingsModel = getReferencedModelByDatasetType(
                    destinationDatasetType,
                    FormatWriteSettings.MODEL
                );
                if (resource.typeProperties.sink.formatSettings != null) {
                    resource.typeProperties.sink.formatSettings.type = formatSettingsModel?.name;
                } else if (formatSettingsModel != null) {
                    resource.typeProperties.sink.formatSettings = {
                        type: formatSettingsModel.name
                    };
                }

                this._processFileFormatSettingsToModel(activity, resource, destinationDatasetType, 'destination');
            }
            // endregion

            // region handle write behavior(table action option)
            if (
                !stringIsNullOrEmpty(activity.properties.destination.tableActionOption) &&
                stringIsNullOrEmpty(activity.properties.destination.writeBehavior)
            ) {
                // convert tableActionOption to writeBehavior
                const tableActionOption = activity.properties.destination.tableActionOption;
                resource.typeProperties.sink.writeBehavior = convertTableActionOptionToWriteBehavior(tableActionOption);
            } else if (
                stringIsNullOrEmpty(activity.properties.destination.writeBehavior) &&
                copySinkType === adfModelTypeMap.DataWarehouse.copySinkType
            ) {
                // For the previously created copy job, set write behavior to insert
                resource.typeProperties.sink.writeBehavior = copyMethodOptionValues.BulkInsert;
            }
            if (!stringIsNullOrEmpty(activity.properties.destination.writeBehavior)) {
                let writeBehavior = activity.properties.destination.writeBehavior;
                // convert old writeBehavior(insert, upsert) to current writeBehavior(Append, Upsert)
                writeBehavior = convertSQLWriteBehaviorToCopyJobWriteBehavior(writeBehavior);

                if (isAncestor(SqlSink.MODEL, copySinkModel)) {
                    switch (writeBehavior) {
                        case 'Append':
                            resource.typeProperties.sink.writeBehavior = 'insert';
                            break;
                        case 'Upsert':
                            resource.typeProperties.sink.writeBehavior = 'upsert';
                            break;
                        case 'SCD2':
                            resource.typeProperties.sink.writeBehavior = 'SCD2';
                            break;
                        default:
                            break;
                    }
                } else {
                    switch (copySinkType) {
                        case adfModelTypeMap.lakehouseTable.copySinkType: {
                            switch (writeBehavior) {
                                case 'Append':
                                    resource.typeProperties.sink.tableActionOption = 'Append';
                                    break;
                                case 'Overwrite':
                                    resource.typeProperties.sink.tableActionOption = 'OverwriteSchema';
                                    break;
                                case 'Upsert':
                                    resource.typeProperties.sink.tableActionOption = 'Upsert';
                                    break;
                                case 'SCD2':
                                    resource.typeProperties.sink.tableActionOption = 'SCD2';
                                    break;
                                default:
                                    break;
                            }
                            Reflect.deleteProperty(resource.typeProperties.sink, 'writeBehavior');
                            break;
                        }
                        case adfModelTypeMap.azureSqlDW.copySinkType: {
                            const sqlDwSink = resource.typeProperties.sink as Model.SqlDWSink;
                            switch (writeBehavior) {
                                case 'Append':
                                    sqlDwSink.allowCopyCommand = true;
                                    break;
                                case 'Upsert':
                                    sqlDwSink.writeBehavior = 'Upsert';
                                    break;
                                case 'SCD2':
                                    sqlDwSink.writeBehavior = 'SCD2';
                                    break;
                                default:
                                    break;
                            }
                            break;
                        }
                        case adfModelTypeMap.DataWarehouse.copySinkType: {
                            switch (writeBehavior) {
                                case 'Append':
                                    resource.typeProperties.sink.writeBehavior = copyMethodOptionValues.BulkInsert;
                                    break;
                                case 'Upsert':
                                    resource.typeProperties.sink.writeBehavior = copyMethodOptionValues.Upsert;
                                    break;
                                case 'SCD2':
                                    resource.typeProperties.sink.writeBehavior = copyMethodOptionValues.SCD2;
                                    break;
                                default:
                                    break;
                            }
                            break;
                        }
                        case adfModelTypeMap.dynamics.copySinkType:
                        case adfModelTypeMap.dynamicsCRM.copySinkType:
                        case adfModelTypeMap.commonDataService.copySinkType: {
                            if (writeBehavior === 'Upsert') {
                                const dynamicsSink = resource.typeProperties.sink as Model.DynamicsSink;
                                dynamicsSink.writeBehavior = writeBehaviorOptionValues.upsert;
                                if (
                                    isArray(activity.properties.destination.upsertSettings?.keys) &&
                                    !stringIsNullOrEmpty(head(activity.properties.destination.upsertSettings.keys))
                                ) {
                                    dynamicsSink.alternateKeyName = head(
                                        activity.properties.destination.upsertSettings.keys
                                    );
                                }
                            }
                            break;
                        }
                        case adfModelTypeMap.salesforceV2.copySinkType:
                        case adfModelTypeMap.salesforceServiceCloudV2.copySinkType: {
                            if (writeBehavior === 'Upsert') {
                                const salesforceSink = resource.typeProperties.sink as Model.SalesforceV2Sink;
                                salesforceSink.writeBehavior = writeBehaviorOptionValues.upsert;
                                if (
                                    isArray(activity.properties.destination.upsertSettings?.keys) &&
                                    !stringIsNullOrEmpty(head(activity.properties.destination.upsertSettings.keys))
                                ) {
                                    salesforceSink.externalIdFieldName = head(
                                        activity.properties.destination.upsertSettings.keys
                                    );
                                }
                            }
                            break;
                        }
                        case adfModelTypeMap.cosmosSql.copySinkType:
                        case adfModelTypeMap.cosmosMongo.copySinkType:
                        case adfModelTypeMap.mongoDb.copySinkType:
                        case adfModelTypeMap.mongoDbAtlas.copySinkType:
                        case adfModelTypeMap.oracle.copySinkType:
                        case adfModelTypeMap.amazonRedshift.copySinkType: {
                            switch (writeBehavior) {
                                case 'Append':
                                    (
                                        resource.typeProperties.sink as
                                        | Model.CosmosDbSqlApiSink
                                        | Model.DocumentDbMongoDbApiCollectionSink
                                        | Model.NativeMongoDbSink
                                        | Model.MongoDbAtlasSink
                                        | Model.OracleSink
                                        | Model.AmazonRedshiftSink
                                    ).writeBehavior = writeBehaviorOptionValues.insert;
                                    break;
                                case 'Upsert':
                                    (
                                        resource.typeProperties.sink as
                                        | Model.CosmosDbSqlApiSink
                                        | Model.DocumentDbMongoDbApiCollectionSink
                                        | Model.NativeMongoDbSink
                                        | Model.MongoDbAtlasSink
                                    ).writeBehavior = writeBehaviorOptionValues.upsert;
                                    break;
                                case 'SCD2':
                                    (
                                        resource.typeProperties.sink as
                                        | Model.CosmosDbSqlApiSink
                                        | Model.DocumentDbMongoDbApiCollectionSink
                                        | Model.NativeMongoDbSink
                                        | Model.MongoDbAtlasSink
                                    ).writeBehavior = writeBehaviorOptionValues.SCD2;
                                    break;
                                default:
                                    break;
                            }
                            break;
                        }
                        case adfModelTypeMap.azureSearch.copySinkType: {
                            const searchSink = resource.typeProperties.sink as Model.AzureSearchIndexSink;
                            switch (writeBehavior) {
                                case 'Merge':
                                    searchSink.indexAction = writeBehaviorOptionValues.merge;
                                    break;
                                case 'Replace':
                                    searchSink.indexAction = writeBehaviorOptionValues.upload;
                                    break;
                                default:
                                    break;
                            }
                            break;
                        }
                        case adfModelTypeMap.snowflakeV2.copySinkType: {
                            if (writeBehavior === UpdateMethod.Upsert) {
                                (resource.typeProperties.sink as Model.SnowflakeV2Sink).writeBehavior =
                                    writeBehaviorOptionValues.upsert;
                            }
                            break;
                        }
                        case adfModelTypeMap.azurePostgreSQL.copySinkType: {
                            if (GlobalVariables.cjAzurePGv2) {
                                // map normalized copy-job `writeBehavior` (Append/Upsert) to
                                // AzurePostgreSql sink resource `writeMethod` (CopyCommand/Upsert);
                                // copy jobs have no BulkInsert, so Append maps to CopyCommand.
                                const postgreSink = resource.typeProperties.sink as Model.AzurePostgreSQLSink;
                                switch (writeBehavior) {
                                    case 'Append':
                                        postgreSink.writeMethod = 'CopyCommand';
                                        break;
                                    case 'Upsert':
                                        postgreSink.writeMethod = 'Upsert';
                                        if (isArray(activity.properties.destination.upsertSettings?.keys)) {
                                            postgreSink.upsertSettings = {
                                                keys: activity.properties.destination.upsertSettings.keys
                                            };
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            }
                            break;
                        }
                        default:
                            break;
                    }
                }
            }
            // endregion

            this._jobQueryToActivityQuery(copySourceType, tableAlias, queryAlias, resource);

            if (sourceDatasetType === adfModelTypeMap.cosmosSql.datasetType) {
                resource.typeProperties.source.datasetSettings.typeProperties.collectionName =
                    activity.properties.source.datasetSettings.container;
            }

            if (destinationDatasetType === adfModelTypeMap.cosmosSql.datasetType) {
                resource.typeProperties.sink.datasetSettings.typeProperties.collectionName =
                    activity.properties.destination.datasetSettings.container;
            }

            if (sourceDatasetType === adfModelTypeMap.sharePointOnlineList.datasetType) {
                resource.typeProperties.source.datasetSettings.typeProperties.listName =
                    activity.properties.source.datasetSettings.list;
            }

            if (destinationDatasetType === adfModelTypeMap.azureSearch.datasetType) {
                resource.typeProperties.sink.datasetSettings.typeProperties.indexName =
                    activity.properties.destination.datasetSettings.index;
            }

            if (sourceDatasetType === adfModelTypeMap.snowflakeV2.datasetType) {
                if (!stringIsNullOrEmpty(copyJob.properties.source?.additionalSettings?.storageIntegration)) {
                    (resource.typeProperties.source as Model.SnowflakeSource).exportSettings = {
                        storageIntegration: copyJob.properties.source.additionalSettings.storageIntegration
                    };
                }
            }

            if (destinationDatasetType === adfModelTypeMap.snowflakeV2.datasetType) {
                if (!stringIsNullOrEmpty(copyJob.properties.destination?.additionalSettings?.storageIntegration)) {
                    (resource.typeProperties.sink as Model.SnowflakeSink).importSettings = {
                        storageIntegration: copyJob.properties.destination.additionalSettings.storageIntegration
                    };
                }
            }

            if (copyJob.properties.stagingSettings != null) {
                resource.typeProperties.stagingSettings = copyJob.properties.stagingSettings;
            }

            // #region: handle protocol settings
            if (
                [
                    adfModelTypeMap.rest.copySourceType,
                    adfModelTypeMap.odata.copySourceType,
                    adfModelTypeMap.dynamicsAX.copySourceType,
                    adfModelTypeMap.sharePointOnlineList.copySourceType
                ].includes(copySourceType)
            ) {
                const resourceSource = resource.typeProperties.source as
                    | Model.RestSource
                    | Model.ODataSource
                    | Model.DynamicsAXSource
                    | Model.SharePointOnlineListCopySource;
                // Will honor the protocol settings from the activity first, then from the copy job properties.
                let protocolSettingsItems = undefined;
                if (activity.properties.source?.protocolSettings != null) {
                    protocolSettingsItems = activity.properties.source.protocolSettings;
                } else if (copyJob.properties.source?.additionalSettings?.protocolSettings != null) {
                    protocolSettingsItems = copyJob.properties.source.additionalSettings.protocolSettings;
                }
                if (protocolSettingsItems != null) {
                    resourceSource.httpRequestTimeout = protocolSettingsItems.requestTimeout;
                    if (sourceDatasetType === adfModelTypeMap.rest.datasetType) {
                        resourceSource.requestInterval = protocolSettingsItems.requestInterval;
                        resourceSource.requestMethod = protocolSettingsItems.requestMethod;
                        resourceSource.requestBody = protocolSettingsItems.requestBody;
                        resourceSource.additionalHeaders = protocolSettingsItems.additionalHeaders;
                        resourceSource.paginationRules = protocolSettingsItems.paginationRules;
                    }
                }
            }

            if (copySinkType === adfModelTypeMap.rest.copySinkType) {
                const resourceSink = resource.typeProperties.sink as Model.RestSink;
                // Will honor the protocol settings from the activity first, then from the copy job properties.
                let protocolSettingsItems = undefined;
                if (activity.properties.destination?.protocolSettings != null) {
                    protocolSettingsItems = activity.properties.destination.protocolSettings;
                } else if (copyJob.properties.destination?.additionalSettings?.protocolSettings != null) {
                    protocolSettingsItems = copyJob.properties.destination.additionalSettings.protocolSettings;
                }

                if (protocolSettingsItems != null) {
                    const requestInterval =
                        typeof protocolSettingsItems.requestInterval === 'string'
                            ? moment.duration(protocolSettingsItems.requestInterval).asMilliseconds()
                            : protocolSettingsItems.requestInterval;

                    resourceSink.httpRequestTimeout = protocolSettingsItems.requestTimeout;
                    resourceSink.requestInterval = requestInterval;
                    resourceSink.requestMethod = protocolSettingsItems.requestMethod;
                    resourceSink.writeBatchSize = protocolSettingsItems.writeBatchSize;
                    resourceSink.additionalHeaders = protocolSettingsItems.additionalHeaders;
                    resourceSink.httpCompressionType = protocolSettingsItems.compressionType;
                }
            }
            // #endregion: handle protocol settings

            return resource;
        });
    }

    public override async toFabricCopilotContext(entity: CopyJob): Promise<CopyJobDefinitionContext | undefined> {
        if (!entity) {
            return undefined;
        }

        const context: CopyJobDefinitionContext = {
            Activities: []
        };

        if (entity.activities?.size > 0) {
            const activityPromises = Array.from(entity.activities).map(async (activity) => {
                const adapter = this.context.asyncAdapterFor<
                    CopyJobActivity,
                    Model.CopyJobActivity,
                    CopyJobActivityContext
                >(Metamodel.modelOf(activity));
                return adapter.toFabricCopilotContext(activity);
            });

            const activityContexts = (await Promise.all(activityPromises)).filter(
                (context): context is CopyJobActivityContext => !!context
            );

            if (activityContexts.length > 0) {
                context.Activities = activityContexts;
            }
        }

        return context;
    }

    protected override async _toResource(named: CopyJob): Promise<Model.CopyJobResource> {
        const activityArray = Array.from(named.activities);
        if (activityArray.length === 0) {
            return {
                name: named.name,
                objectId: named.objectId,
                properties: {
                    jobMode: named.mode,
                    source: undefined,
                    destination: undefined,
                    stagingSettings: undefined,
                    policy: {
                        timeout: named.timeout ?? '0.12:00:00',
                        retry: named.retry
                    },
                    dataFactoryId: named.dataFactoryId
                },
                activities: []
            };
        }

        const sourceDataset = head(activityArray)?.sourceDataset;
        const destinationDataset = head(activityArray)?.sinkDataset;
        const sourceDatasetType = Metamodel.modelOf(sourceDataset)?.name;
        const destinationDatasetType = Metamodel.modelOf(destinationDataset)?.name;
        const datasetRegistryService = await getServiceInstance<DatasetRegistryService>(
            this.context.adapterConfig.contextData,
            'DatasetRegistryService'
        );
        const iframeService = await getServiceInstance<IFrameService>(
            this.context.adapterConfig.contextData,
            'IFrameService'
        );

        let sourceConnectionSettings: Model.CopyJobResource['properties']['source']['connectionSettings'];
        if (named.source != null) {
            if (named.source.isWorkspaceBuiltIn || named.source.isSampleDatasetConnection) {
                const sourceLinkedServiceResource = await convertToResourceAsync<Model.LinkedServiceResource>(
                    this.context.contextData,
                    named.source
                );

                if (named.sourceDmtsCredentialForArtifact != null) {
                    sourceLinkedServiceResource.properties.linkedServiceName = linkedServiceReferenceToResource(
                        named.sourceDmtsCredentialForArtifact,
                        this.context,
                        this.ensureLogger()
                    );
                }
                sourceConnectionSettings = sourceLinkedServiceResource.properties;
                if (named.source.isSampleDatasetConnection) {
                    Reflect.deleteProperty(sourceConnectionSettings.typeProperties, 'authenticationType');
                }
                if (sourceLinkedServiceResource.properties.type === adfModelTypeMap.lakehouse.linkedServiceType) {
                    // For Lakehouse, we have to use the dataset type to determine the root folder.
                    sourceLinkedServiceResource.properties.typeProperties.rootFolder =
                        sourceDatasetType === adfModelTypeMap.lakehouseTable.datasetType ? 'Tables' : 'Files';
                    // Clone delta table uses Binary source type but reads from Tables root folder.
                    if (named.isCloneDeltaTable && sourceDatasetType === adfModelTypeMap.binary.datasetType) {
                        sourceLinkedServiceResource.properties.typeProperties.rootFolder = 'Tables';
                    }
                }
                Reflect.deleteProperty(sourceConnectionSettings, 'annotations');
            } else {
                const sourceLinkedServiceType = getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
                    sourceDataset,
                    named.source.tridentConnectionKind,
                    {
                        iframeService,
                        datasetRegistry: datasetRegistryService
                    }
                );
                sourceConnectionSettings = {
                    type: sourceLinkedServiceType,
                    linkedServiceName: linkedServiceReferenceToResource(named.source, this.context, this.ensureLogger())
                };

                if (
                    GlobalVariables.snowflakeRoleCopyJob &&
                    sourceLinkedServiceType === adfModelTypeMap.snowflakeV2.linkedServiceType &&
                    sourceDataset.linkedServiceProperties
                ) {
                    sourceConnectionSettings.connectionProperties = {};
                    sourceDataset.linkedServiceProperties.forEach((property) => {
                        sourceConnectionSettings.connectionProperties[property.name] = property.value;
                    });
                }

                if (connectionSupportEnumerateDatabase(named.source.tridentConnectionKind)) {
                    const sourceDatasetMetadata = datasetRegistryService.getDatasetConfiguration(sourceDatasetType);
                    const databaseAlias = sourceDatasetMetadata?.databaseAlias ?? 'database';
                    let database: string;
                    for (const activity of activityArray) {
                        const sourceDatasetResource = await convertToResourceAsync(
                            this.context.adapterConfig.contextData,
                            activity.sourceDataset
                        );
                        database = sourceDatasetResource.properties.typeProperties[databaseAlias] as string;
                        if (!stringIsNullOrEmpty(database)) {
                            break;
                        }
                    }
                    if (!stringIsNullOrEmpty(database)) {
                        sourceConnectionSettings.typeProperties = { [databaseAlias]: database };
                    }
                }
                if (named.useAbap) {
                    if (sourceConnectionSettings.typeProperties != null) {
                        sourceConnectionSettings.typeProperties.useAbap = true;
                    } else {
                        sourceConnectionSettings.typeProperties = { useAbap: true };
                    }
                }
            }
        }

        let sourceAdditionalSettings: Model.CopyJobResource['properties']['source']['additionalSettings'];
        if (sourceDatasetType === adfModelTypeMap.snowflakeV2.datasetType) {
            const source = head(activityArray)?.source as SnowflakeV2Source;
            if (!stringIsNullOrEmpty(source?.exportSettings?.storageIntegration)) {
                sourceAdditionalSettings = {
                    storageIntegration: source.exportSettings.storageIntegration
                };
            }
            if (GlobalVariables.snowflakeCDCSharedDbCopyJob) {
                if (!stringIsNullOrEmpty(named.dbNameForStagingStreams)) {
                    sourceAdditionalSettings = {
                        ...sourceAdditionalSettings,
                        dbNameForStagingStreams: named.dbNameForStagingStreams
                    };
                }
                if (!stringIsNullOrEmpty(named.schemaNameForStagingStreams)) {
                    sourceAdditionalSettings = {
                        ...sourceAdditionalSettings,
                        schemaNameForStagingStreams: named.schemaNameForStagingStreams
                    };
                }
            }
        }

        let destinationConnectionSettings: Model.CopyJobResource['properties']['destination']['connectionSettings'];
        if (named.destination != null) {
            if (named.destination.isWorkspaceBuiltIn) {
                const destinationLinkedServiceResource = await convertToResourceAsync<Model.LinkedServiceResource>(
                    this.context.contextData,
                    named.destination
                );
                if (named.destinationDmtsCredentialForArtifact != null) {
                    destinationLinkedServiceResource.properties.linkedServiceName = linkedServiceReferenceToResource(
                        named.destinationDmtsCredentialForArtifact,
                        this.context,
                        this.ensureLogger()
                    );
                }
                destinationConnectionSettings = destinationLinkedServiceResource.properties;
                if (destinationLinkedServiceResource.properties.type === adfModelTypeMap.lakehouse.linkedServiceType) {
                    // For Lakehouse, we have to use the dataset type to determine the root folder.
                    destinationLinkedServiceResource.properties.typeProperties.rootFolder =
                        destinationDatasetType === adfModelTypeMap.lakehouseTable.datasetType ? 'Tables' : 'Files';
                }
                // EventStream uses a different connectionSettings.type for sink ('EventStreamSink')
                // to distinguish sink behavior from source on the backend.
                if (
                    destinationLinkedServiceResource.properties.type === adfModelTypeMap.eventStream.linkedServiceType
                ) {
                    destinationConnectionSettings.type = adfModelTypeMap.eventStream.copySinkType;
                }
                Reflect.deleteProperty(destinationConnectionSettings, 'annotations');
            } else {
                const destinationLinkedServiceType = getLinkedServiceTypeFromDatasetMetadataAndConnectionKind(
                    destinationDataset,
                    named.destination.tridentConnectionKind,
                    {
                        iframeService,
                        datasetRegistry: datasetRegistryService
                    }
                );
                destinationConnectionSettings = {
                    type: destinationLinkedServiceType,
                    linkedServiceName: linkedServiceReferenceToResource(
                        named.destination,
                        this.context,
                        this.ensureLogger()
                    )
                };

                if (
                    GlobalVariables.snowflakeRoleCopyJob &&
                    destinationLinkedServiceType === adfModelTypeMap.snowflakeV2.linkedServiceType &&
                    destinationDataset.linkedServiceProperties
                ) {
                    destinationConnectionSettings.connectionProperties = {};
                    destinationDataset.linkedServiceProperties.forEach((property) => {
                        destinationConnectionSettings.connectionProperties[property.name] = property.value;
                    });
                }

                if (connectionSupportEnumerateDatabase(named.destination.tridentConnectionKind)) {
                    const destinationDatasetMetadata =
                        datasetRegistryService.getDatasetConfiguration(destinationDatasetType);
                    const databaseAlias = destinationDatasetMetadata?.databaseAlias ?? 'database';
                    let database: string;
                    for (const activity of activityArray) {
                        const destinationDatasetResource = await convertToResourceAsync(
                            this.context.adapterConfig.contextData,
                            activity.sinkDataset
                        );
                        database = destinationDatasetResource.properties.typeProperties[databaseAlias] as string;
                        if (!stringIsNullOrEmpty(database)) {
                            break;
                        }
                    }
                    if (!stringIsNullOrEmpty(database)) {
                        destinationConnectionSettings.typeProperties = { [databaseAlias]: database };
                    }
                }
            }
        }

        let destinationAdditionalSettings: Model.CopyJobResource['properties']['destination']['additionalSettings'];
        if (destinationDatasetType === adfModelTypeMap.snowflakeV2.datasetType) {
            const destination = head(activityArray)?.copySink as SnowflakeV2Sink;
            if (!stringIsNullOrEmpty(destination?.importSettings?.storageIntegration)) {
                destinationAdditionalSettings = {
                    storageIntegration: destination.importSettings.storageIntegration
                };
            }
        }

        let stagingSettings: Model.CopyJobResource['properties']['stagingSettings'];
        {
            const activityStagingSettings = head(activityArray)?.stagingSettings;
            if (activityStagingSettings) {
                const stagingSettingsObject: Model.StagingSettings = {
                    linkedServiceName: linkedServiceReferenceToResource(
                        activityStagingSettings.storageLinkedService,
                        this.context,
                        this.ensureLogger()
                    ),
                    path: activityStagingSettings.path,
                    enableCompression: activityStagingSettings.enableCompression
                };
                stagingSettings = stagingSettingsObject.linkedServiceName ? stagingSettingsObject : undefined;
            }
        }

        let genericChangeDataProperties: Model.CopyJobResource['properties']['source']['genericChangeDataProperties'];
        if (GlobalVariables.copyJobGenericCdc && named.genericChangeDataProperties) {
            const props = named.genericChangeDataProperties;
            const rowMarkerValuesObject: Model.CopyJobResource['properties']['source']['genericChangeDataProperties']['rowMarkerValues'] =
            {
                insert: props.rowMarkerValues?.insert || undefined,
                update_postimage: props.rowMarkerValues?.update_postimage || undefined,
                update_preimage: props.rowMarkerValues?.update_preimage || undefined,
                delete: props.rowMarkerValues?.delete || undefined
            };
            const hasAnyMarkerValue = Object.values(rowMarkerValuesObject).some((v) => v != null);
            const genericChangeDataPropertiesObject: Model.CopyJobResource['properties']['source']['genericChangeDataProperties'] =
            {
                rowMarkerColumn: props.rowMarkerColumn || undefined,
                rowOrderColumn: props.rowOrderColumn || undefined,
                initialFolderName: props.initialFolderName || undefined,
                deltaFolderName: props.deltaFolderName || undefined,
                rowMarkerValues: hasAnyMarkerValue ? rowMarkerValuesObject : undefined
            };
            genericChangeDataProperties = Object.values(genericChangeDataPropertiesObject).some((v) => v != null)
                ? genericChangeDataPropertiesObject
                : undefined;
        }

        const copyJobActivities: Model.CopyJobActivity[] = await Promise.all(
            activityArray.map((activity) => {
                if (Metamodel.modelOf(activity.source)?.name === adfModelTypeMap.oracleFusion.copySourceType) {
                    const source = activity.source as OracleFusionCopySource;
                    if (stringIsNullOrEmpty(source.biccSavedJobLabel)) {
                        source.biccSavedJobLabel = crypto.randomUUID().replaceAll('-', '');
                    }
                }
                return convertToResourceAsync<Model.CopyJobActivity>(this.context.contextData, activity);
            })
        );
        const activities = await this._convertCopyActivityResourceToCopyJobResource(copyJobActivities, named);

        let defaultActivityProperties: Model.CopyJobResource['properties']['defaultActivityProperties'];
        if (GlobalVariables.copyJobCustomStagingSchema) {
            const stagingSchema = !stringIsNullOrEmpty(named.schemaNameForStagingTables)
                ? named.schemaNameForStagingTables
                : undefined;
            if (stagingSchema != null) {
                defaultActivityProperties = {
                    destination: {
                        schemaNameForStagingTables: stagingSchema
                    }
                };
            }
        }

        if (named.mode === CopyJobMode.IncrementalCopy) {
            for (const activity of activities) {
                if (
                    GlobalVariables.copyJobRefreshWithoutIncrementalColumn &&
                    activity.properties.source.changeReadStrategy === ChangeReadStrategy.Batch
                ) {
                    // Batch tables read all data every run and intentionally omit
                    // changeDataSettings, so skip the default read-method backfill.
                    continue;
                }
                if (stringIsNullOrEmpty(activity.properties.source.changeDataSettings?.readMethod)) {
                    if (activity.properties.source.changeDataSettings == null) {
                        activity.properties.source.changeDataSettings = {
                            readMethod: ReadMethod.Default
                        };
                    } else {
                        activity.properties.source.changeDataSettings.readMethod = ReadMethod.Default;
                    }
                }
                if (
                    GlobalVariables.copyJobNullRecords &&
                    activity.properties.source.changeDataSettings?.columns?.length &&
                    activity.properties.source.changeDataSettings.nullWatermarkBehavior == null
                ) {
                    activity.properties.source.changeDataSettings.nullWatermarkBehavior = NullWatermarkBehavior.Skip;
                }
            }
        }

        // #region: handle protocol settings
        if (
            [
                adfModelTypeMap.rest.datasetType,
                adfModelTypeMap.odata.datasetType,
                adfModelTypeMap.dynamicsAX.datasetType,
                adfModelTypeMap.sharePointOnlineList.datasetType
            ].includes(sourceDatasetType) &&
            head(activities)?.properties.source.protocolSettings != null
        ) {
            const sourceProtocolSettings = Object.fromEntries(
                Object.entries(head(activities)?.properties.source.protocolSettings ?? {})
            );
            sourceAdditionalSettings = {
                ...sourceAdditionalSettings,
                protocolSettings: sourceProtocolSettings
            };
        }

        if (
            destinationDatasetType === adfModelTypeMap.rest.datasetType &&
            head(activities)?.properties.destination.protocolSettings != null
        ) {
            const destinationProtocolSettings = Object.fromEntries(
                Object.entries(head(activities)?.properties.destination.protocolSettings ?? {})
            );
            destinationAdditionalSettings = {
                ...destinationAdditionalSettings,
                protocolSettings: destinationProtocolSettings
            };
        }
        // #endregion: handle protocol settings

        const libraryVariables = Object.fromEntries(
            Array.from(named.libraryVariables).map(({ name, libraryName, variableName }) => [
                name,
                {
                    libraryName,
                    variableName
                }
            ])
        );

        // region: AzurePostgreSql v2 version on the copy job source/destination.
        // V2 enables user-auth (source & destination) and Upsert (destination). The version is
        // taken from the dataset model `version`, which the copy-job deployment service stamps as
        // '2.0' for new copy jobs (and whenever the destination uses Upsert) while preserving an
        // existing copy job's original version (undefined for legacy v1 jobs).
        let sourceVersion: string;
        let destinationVersion: string;
        if (GlobalVariables.cjAzurePGv2) {
            if (sourceDatasetType === adfModelTypeMap.azurePostgreSQL.datasetType) {
                sourceVersion = (sourceDataset as { version?: string })?.version;
            }
            if (destinationDatasetType === adfModelTypeMap.azurePostgreSQL.datasetType) {
                destinationVersion = (destinationDataset as { version?: string })?.version;
            }
        }
        // endregion

        const resource = {
            name: named.name,
            objectId: named.objectId,
            properties: {
                jobMode: named.mode,
                buildFeatures: sourceDatasetType === adfModelTypeMap.oracleFusion.datasetType
                    ? ['RequireMsdiV2Source']
                    : undefined,
                fullLoadBehavior: named.mode === CopyJobMode.IncrementalCopy && !activityArray.some(isDeltaCloneCopy)
                    ? named.fullLoadBehavior
                    : undefined,
                useSharedCompute:
                    GlobalVariables.copyJobUseSharedCompute && named.mode === CopyJobMode.IncrementalCopy
                        ? named.useSharedCompute
                        : undefined,
                source: {
                    type: sourceDatasetType,
                    version: sourceVersion,
                    connectionSettings: sourceConnectionSettings,
                    additionalSettings: sourceAdditionalSettings,
                    genericChangeDataProperties
                },
                destination: {
                    type: destinationDatasetType,
                    version: destinationVersion,
                    connectionSettings: destinationConnectionSettings,
                    additionalSettings: destinationAdditionalSettings
                },
                stagingSettings,
                defaultActivityProperties,
                policy: {
                    timeout: named.timeout ?? '0.12:00:00',
                    retry: named.retry
                },
                dataFactoryId: named.dataFactoryId
            },
            activities,
            libraryVariables
        };

        try {
            if (named.raw) {
                merge(resource, JSON.parse(named.raw));
            }
        } catch (e) {
            this._logError('Failed to merge raw in copy job adapter to resource', e);
        }

        if (sourceDatasetType === adfModelTypeMap.oracleFusion.datasetType &&
            !resource.properties.buildFeatures?.includes('RequireMsdiV2Source')) {
            resource.properties.buildFeatures = [
                ...(resource.properties.buildFeatures ?? []),
                'RequireMsdiV2Source'
            ];
        }

        return resource;
    }

    protected override async _toModel(resource: Model.CopyJobResource, named?: CopyJob): Promise<CopyJob> {
        if (named == null) {
            named = CopyJob.MODEL.create();
        }
        await super._toModel(resource, named);

        // Explicitly preserve dataFactoryId
        if (resource.properties?.dataFactoryId) {
            named.dataFactoryId = resource.properties.dataFactoryId;
        }

        if (
            resource.properties?.source != null &&
            resource.properties?.destination != null &&
            resource.activities != null
        ) {
            const activityResources = await this.convertCopyJobResourceToCopyActivityResource(resource);
            const activities: CopyJobActivity[] = await Promise.all(
                activityResources.map((activity) =>
                    convertToModelAsync<CopyJobActivity>(this.context.contextData, activity.name, activity, true)
                )
            );
            named.activities.clear();
            activities.forEach((activity) => {
                if (stringIsNullOrEmpty(activity.name)) {
                    activity.name = NEW_COPY_JOB_ACTIVITY_NAME;
                }
                if (
                    resource.properties.jobMode === CopyJobMode.IncrementalCopy &&
                    stringIsNullOrEmpty(activity.readMethod)
                ) {
                    activity.readMethod = ReadMethod.Default;
                }
                if (
                    GlobalVariables.copyJobNullRecords &&
                    resource.properties.jobMode === CopyJobMode.IncrementalCopy &&
                    activity.columns.length &&
                    activity.nullWatermarkBehavior == null
                ) {
                    activity.nullWatermarkBehavior = NullWatermarkBehavior.Skip;
                }
                named.activities.add(activity);
            });
            const sourceDataset = head(activities)?.sourceDataset;
            const sinkDataset = head(activities)?.sinkDataset;

            // Preserve the persisted AzurePostgreSql version on the loaded dataset models. The
            // dataset model defaults to '2.0' in Fabric, so without this an existing v1 copy job
            // (no version) would look like a v2 job. Explicitly stamping the persisted value keeps
            // the loaded model faithful so editing the job does not silently upgrade it.
            if (GlobalVariables.cjAzurePGv2) {
                activities.forEach((activity) => {
                    if (
                        Metamodel.modelOf(activity.sourceDataset)?.name ===
                        adfModelTypeMap.azurePostgreSQL.datasetType
                    ) {
                        (activity.sourceDataset as { version?: string }).version =
                            resource.properties.source.version;
                    }
                    if (
                        Metamodel.modelOf(activity.sinkDataset)?.name ===
                        adfModelTypeMap.azurePostgreSQL.datasetType
                    ) {
                        (activity.sinkDataset as { version?: string }).version =
                            resource.properties.destination.version;
                    }
                });
            }

            named.source = sourceDataset?.linkedService;
            named.destination = sinkDataset?.linkedService;
            named.sourceDmtsCredentialForArtifact = sourceDataset?.dmtsCredentialForArtifact;
            named.destinationDmtsCredentialForArtifact = sinkDataset?.dmtsCredentialForArtifact;
            if (GlobalVariables.snowflakeRoleCopyJob) {
                if (named.source) {
                    sourceDataset.linkedServiceProperties?.forEach((property) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (named.source as any)[property.name] = property.value;
                    });
                }
                if (named.destination) {
                    sinkDataset.linkedServiceProperties?.forEach((property) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (named.destination as any)[property.name] = property.value;
                    });
                }
            }
            named.mode = resource.properties.jobMode as CopyJobMode;
            named.fullLoadBehavior = resource.properties.fullLoadBehavior;

            if (
                GlobalVariables.copyJobUseSharedCompute &&
                resource.properties.jobMode === CopyJobMode.IncrementalCopy
            ) {
                named.useSharedCompute = resource.properties.useSharedCompute;
            }

            if (GlobalVariables.copyJobGenericCdc && resource.properties?.source?.genericChangeDataProperties) {
                named.genericChangeDataProperties = resource.properties.source.genericChangeDataProperties;
            }

            if (resource.properties.source?.connectionSettings?.typeProperties?.useAbap) {
                named.useAbap = true;
            }

            if (GlobalVariables.snowflakeCDCSharedDbCopyJob) {
                const sourceAdditionalSettings = resource.properties?.source?.additionalSettings;
                if (!stringIsNullOrEmpty(sourceAdditionalSettings?.dbNameForStagingStreams)) {
                    named.dbNameForStagingStreams = sourceAdditionalSettings.dbNameForStagingStreams;
                }
                if (!stringIsNullOrEmpty(sourceAdditionalSettings?.schemaNameForStagingStreams)) {
                    named.schemaNameForStagingStreams = sourceAdditionalSettings.schemaNameForStagingStreams;
                }
            }

            if (GlobalVariables.copyJobCustomStagingSchema) {
                const stagingSchema =
                    resource.properties?.defaultActivityProperties?.destination?.schemaNameForStagingTables;
                if (!stringIsNullOrEmpty(stagingSchema)) {
                    named.schemaNameForStagingTables = stagingSchema;
                }
            }

            if (!stringIsNullOrEmpty(resource.properties.policy?.timeout)) {
                named.timeout = resource.properties.policy.timeout;
            }

            if (resource.properties.policy?.retry !== undefined) {
                named.retry = resource.properties.policy.retry;
            } else {
                // since retry has default value 0, so we delete it for existing copy job which has no retry property
                delete named.retry;
            }
        }

        if (resource.libraryVariables) {
            Object.entries(resource.libraryVariables).forEach(([key, value]) => {
                const libraryVariable = Owned.create<LibraryVariable>(LibraryVariable.MODEL, named);
                libraryVariable.name = key;
                libraryVariable.libraryName = value.libraryName;
                libraryVariable.variableName = value.variableName;
                // todo: do not use fixed type
                libraryVariable._type = 'String';
                named.addLibraryVariable(libraryVariable);
            });
        }

        try {
            const rawObject = await ValueAdapter.remainder(this.context, resource, CopyJob.MODEL);
            /*
                In Trident resources, we don't include the type field, but Azure resources require it. Therefore,
                we add type in the toAzureResource process. However, since it appears in the raw data, we need to
                remove it at this stage.
             */
            if (rawObject && typeof rawObject === 'object') {
                Reflect.deleteProperty(rawObject, 'type');
            }
            named.raw = JSON.stringify(rawObject);
        } catch (e) {
            this._logError('Failed to parse raw in copy job adapter to model', e);
        }

        return named;
    }

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<Model.CopyJobResource> {
        let { workloadPayload } = tridentResource;
        if (stringIsNullOrEmpty(workloadPayload)) {
            workloadPayload = '{}';
        }
        let azureResource;
        try {
            azureResource = JSON.parse(workloadPayload);
            azureResource.type = ModelConstants.copyJob;
            azureResource.objectId = tridentResource.objectId;
            azureResource.workspaceObjectId = tridentResource.workspaceObjectId;
            azureResource.name = tridentResource.displayName;
        } catch {
            return undefined;
        }
        try {
            const idParts = azureResource?.id?.split('/');
            idParts[6] = 'Microsoft.DataFactory';
            idParts[7] = 'factories';
            idParts[9] = 'copyjobs';
            idParts[idParts.length - 1] = azureResource.name;
            azureResource.id = idParts.join('/');
        } catch {
            azureResource.id = `/subscriptions/xxxxxxxxx/resourceGroups/xxxx/providers/Microsoft.DataFactory/factories/xxxxxxx/copyjobs/${azureResource.name ?? tridentResource.displayName
                }`;
        }
        const extraInfo: Record<string, unknown> = {};
        Object.keys(tridentResource).forEach((key: keyof ITridentResource) => {
            if (
                ![
                    'description',
                    'displayName',
                    'objectId',
                    'workloadPayload',
                    'payloadContentType',
                    'workspaceObjectId'
                ].includes(key)
            ) {
                extraInfo[key] = tridentResource[key];
            }
        });
        if (azureResource.properties) {
            azureResource.properties.extraTridentInfo = JSON.stringify(extraInfo);
        }
        return azureResource;
    }

    /**
     * convert copy activity resource (ADF) to copy job resource (Fabric), used in to resource
     * @param activities
     * @param entity
     */
    private async _convertCopyActivityResourceToCopyJobResource(
        activities: Model.CopyJobActivity[],
        entity: CopyJob
    ): Promise<Model.CopyJobResource['activities']> {
        const mode = entity.mode;
        const sourceNeedDatabase = connectionSupportEnumerateDatabase(entity.source?.tridentConnectionKind);
        const destinationNeedDatabase = connectionSupportEnumerateDatabase(entity.destination?.tridentConnectionKind);
        const datasetRegistryService = await getServiceInstance<DatasetRegistryService>(
            this.context.adapterConfig.contextData,
            'DatasetRegistryService'
        );
        const lsType = Metamodel.modelOf(entity.source).name;
        const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
            this.context.adapterConfig.contextData,
            'LinkedServiceRegistryService'
        );
        const sourceLinkedServiceMetadata = linkedServiceRegistryService.getLinkedServiceConfiguration(lsType);
        return activities.map((activity) => {
            const resource: Model.CopyJobResource['activities'][number] = {
                id: activity.name,
                properties: {
                    source: {
                        datasetSettings: activity.typeProperties.source.datasetSettings.typeProperties,
                        ...omit(
                            activity.typeProperties.source,
                            'datasetSettings',
                            'type',
                            'additionalColumns',
                            'partitionOption'
                        )
                    },
                    destination: {
                        datasetSettings: activity.typeProperties.sink.datasetSettings.typeProperties,
                        ...omit(activity.typeProperties.sink, 'datasetSettings', 'type')
                    },
                    enableStaging: activity.typeProperties.enableStaging,
                    translator: activity.typeProperties.translator,
                    preserve: activity.typeProperties.preserve,
                    auditColumns: activity.typeProperties.source.additionalColumns,
                    stagingSettings: activity.typeProperties.stagingSettings
                }
            };

            if (activity.typeProperties.source.datasetSettings.type === adfModelTypeMap.oracleFusion.datasetType) {
                resource.properties.source.objectNamespace ??= resource.properties.source.datasetSettings.schema;
                resource.properties.source.objectName ??= resource.properties.source.datasetSettings.table;
            }

            const sourceDatasetMetadata = datasetRegistryService.getDatasetConfiguration(
                activity.typeProperties.source.datasetSettings.type
            );
            if (sourceNeedDatabase) {
                const sourceDatabaseAlias = sourceDatasetMetadata?.databaseAlias ?? 'database';
                Reflect.deleteProperty(resource.properties.source.datasetSettings, sourceDatabaseAlias);
            }

            if (destinationNeedDatabase) {
                const destinationDatasetMetadata = datasetRegistryService.getDatasetConfiguration(
                    activity.typeProperties.sink.datasetSettings.type
                );
                const destinationDatabaseAlias = destinationDatasetMetadata?.databaseAlias ?? 'database';
                Reflect.deleteProperty(resource.properties.destination.datasetSettings, destinationDatabaseAlias);
            }

            if (isArray(activity?.typeProperties?.translator?.mappings)) {
                resource.properties.translator = {
                    ...activity.typeProperties.translator,
                    mappings: activity.typeProperties.translator.mappings.map((mapping) => ({
                        source: mapping?.source,
                        destination: mapping?.sink
                    }))
                };
            }

            if (mode === CopyJobMode.IncrementalCopy) {
                const usesBatchChangeReadStrategy =
                    GlobalVariables.copyJobRefreshWithoutIncrementalColumn &&
                    activity.typeProperties.changeReadStrategy === ChangeReadStrategy.Batch;
                if (usesBatchChangeReadStrategy) {
                    resource.properties.source.changeReadStrategy = ChangeReadStrategy.Batch;
                } else {
                    resource.properties.source.changeDataSettings = {
                        readMethod: activity.typeProperties.readMethod,
                        captureInstanceName:
                            GlobalVariables.copyJobCustomCaptureInstance &&
                            !stringIsNullOrEmpty(activity.typeProperties.captureInstanceName)
                                ? activity.typeProperties.captureInstanceName
                                : undefined,
                        columns: activity.typeProperties.columns
                    };
                    if (
                        GlobalVariables.copyJobNullRecords &&
                        activity.typeProperties.nullWatermarkBehavior != null
                    ) {
                        resource.properties.source.changeDataSettings.nullWatermarkBehavior =
                            activity.typeProperties.nullWatermarkBehavior;
                    }
                }
            }

            if (activity.typeProperties.source.partitionOption) {
                const partitionOption = activity.typeProperties.source.partitionOption;
                resource.properties.source.partitionSettings = {
                    partitionOption: partitionOption === partitionOptionConst.autoDetect ? 'Auto' : partitionOption
                };
            } else {
                resource.properties.source.partitionOption = activity.typeProperties.source.partitionOption;
            }

            if (activity.typeProperties.translator?.typeConversion) {
                resource.properties.typeConversionSettings = {
                    typeConversion: activity.typeProperties.translator.typeConversionSettings
                };
            }

            if (activity.typeProperties.translator?.columnFlattenSettings) {
                resource.properties.columnFlattenSettings = {
                    expandArrayIntoMultipleRows:
                        !activity.typeProperties.translator.columnFlattenSettings.treatArrayAsString,
                    flattenNestedProperties:
                        !activity.typeProperties.translator.columnFlattenSettings.treatStructAsString,
                    flattenColumnDelimiter:
                        activity.typeProperties.translator.columnFlattenSettings.flattenColumnDelimiter
                };
                Reflect.deleteProperty(resource.properties.columnFlattenSettings, 'treatArrayAsString');
                Reflect.deleteProperty(resource.properties.columnFlattenSettings, 'treatStructAsString');
            }

            if (resource.properties.translator != null) {
                resource.properties.translator = pick(resource.properties.translator, 'type', 'mappings');
            }

            this._resolveUpdateMethodInToResource(activity, resource);
            this._resolveQueryInToResource(activity, resource, sourceDatasetMetadata, sourceLinkedServiceMetadata);
            if (
                !isFileTypeDataset(sourceDatasetMetadata) &&
                resource.properties.source.changeDataSettings?.readMethod === 'SnapshotPlusIncremental' &&
                (!isArray(resource.properties.source.changeDataSettings.columns) ||
                    resource.properties.source.changeDataSettings.columns.length === 0) &&
                updateMethodSupportCDC(resource.properties.destination.writeBehavior as UpdateMethodType) &&
                resource.properties.destination.upsertSettings
            ) {
                // Native CDC enabled table doesn't support TempDB, remove the property.
                Reflect.deleteProperty(resource.properties.destination.upsertSettings, 'useTempDB');
            }

            if (activity.typeProperties.source.storeSettings?.type === 'HttpReadSettings') {
                const additionalHeaders = (activity.typeProperties.source.storeSettings as Model.HttpStoreReadSettings)
                    ?.additionalHeaders;
                if (!stringIsNullOrEmpty(additionalHeaders)) {
                    (
                        resource.properties.source.storeSettings as {
                            additionalHeaders?: Record<string, string>;
                        }
                    ).additionalHeaders = parseKeyValueStringToObject(additionalHeaders);
                }
            }

            this._processFileFormatSettingsToResource(activity, resource, 'source');
            this._processFileFormatSettingsToResource(activity, resource, 'destination');

            this._resolveSettingsInToResource(resource);

            // region Convert table name to table
            if (has(resource.properties.source.datasetSettings, 'tableName')) {
                Reflect.set(
                    resource.properties.source.datasetSettings,
                    'table',
                    resource.properties.source.datasetSettings.tableName
                );
                Reflect.deleteProperty(resource.properties.source.datasetSettings, 'tableName');
            }
            if (has(resource.properties.destination.datasetSettings, 'tableName')) {
                Reflect.set(
                    resource.properties.destination.datasetSettings,
                    'table',
                    resource.properties.destination.datasetSettings.tableName
                );
                Reflect.deleteProperty(resource.properties.destination.datasetSettings, 'tableName');
            }
            // endregion

            if (activity.typeProperties.source.datasetSettings.type === adfModelTypeMap.cosmosSql.datasetType) {
                resource.properties.source.datasetSettings.container =
                    activity.typeProperties.source.datasetSettings.typeProperties.collectionName;
                Reflect.deleteProperty(resource.properties.source.datasetSettings, 'collectionName');
            }
            if (activity.typeProperties.sink.datasetSettings.type === adfModelTypeMap.cosmosSql.datasetType) {
                resource.properties.destination.datasetSettings.container =
                    activity.typeProperties.sink.datasetSettings.typeProperties.collectionName;
                Reflect.deleteProperty(resource.properties.destination.datasetSettings, 'collectionName');
            }

            if (
                activity.typeProperties.source.datasetSettings.type === adfModelTypeMap.sharePointOnlineList.datasetType
            ) {
                resource.properties.source.datasetSettings.list =
                    activity.typeProperties.source.datasetSettings.typeProperties.listName;
                Reflect.deleteProperty(resource.properties.source.datasetSettings, 'listName');
            }

            if (activity.typeProperties.sink.datasetSettings.type === adfModelTypeMap.azureSearch.datasetType) {
                resource.properties.destination.datasetSettings.index =
                    activity.typeProperties.sink.datasetSettings.typeProperties.indexName;
                Reflect.deleteProperty(resource.properties.destination.datasetSettings, 'indexName');
            }

            if (activity.typeProperties.source.datasetSettings.type === adfModelTypeMap.snowflakeV2.datasetType) {
                if (
                    !stringIsNullOrEmpty(
                        (activity.typeProperties.source as Model.SnowflakeSource).exportSettings?.storageIntegration
                    )
                ) {
                    Reflect.deleteProperty(resource.properties.source.exportSettings, 'storageIntegration');
                }
                if (
                    GlobalVariables.snowflakeRoleCopyJob &&
                    activity.typeProperties.source?.datasetSettings?.connectionProperties
                ) {
                    resource.properties.source.connectionProperties =
                        activity.typeProperties.source.datasetSettings.connectionProperties;
                    delete activity.typeProperties.source.datasetSettings.connectionProperties;
                }
            }

            if (activity.typeProperties.sink.datasetSettings.type === adfModelTypeMap.snowflakeV2.datasetType) {
                if (
                    !stringIsNullOrEmpty(
                        (activity.typeProperties.sink as Model.SnowflakeSink).importSettings?.storageIntegration
                    )
                ) {
                    Reflect.deleteProperty(resource.properties.destination.importSettings, 'storageIntegration');
                }
                if (
                    GlobalVariables.snowflakeRoleCopyJob &&
                    activity.typeProperties.sink?.datasetSettings?.connectionProperties
                ) {
                    resource.properties.destination.connectionProperties =
                        activity.typeProperties.sink.datasetSettings.connectionProperties;
                    delete activity.typeProperties.sink.datasetSettings.connectionProperties;
                }
            }

            if (activity.typeProperties.stagingSettings != null) {
                Reflect.deleteProperty(resource.properties, 'stagingSettings');
            }

            // handle protocol settings
            this._convertProtocolSettingsToResource(activity, resource);

            return resource;
        });
    }

    private _resolveUpdateMethodInToResource(
        activity: Model.CopyJobActivity,
        resource: Model.CopyJobResource['activities'][number]
    ) {
        const copySinkType = activity.typeProperties.sink.type;
        let writeBehavior: string;
        switch (copySinkType) {
            case adfModelTypeMap.sqlServer.copySinkType:
            case adfModelTypeMap.azureSql.copySinkType:
            case adfModelTypeMap.azureSqlMI.copySinkType:
            case adfModelTypeMap.fabricSql.copySinkType:
                // handle table action option
                writeBehavior = (activity.typeProperties.sink.writeBehavior ?? new SqlSink().writeBehavior) as string;
                resource.properties.destination.writeBehavior =
                    convertSQLWriteBehaviorToCopyJobWriteBehavior(writeBehavior);
                break;
            case adfModelTypeMap.lakehouseTable.copySinkType:
                // for lakehouse, map tableActionOption to writeBehavior
                const tableActionOption = (activity.typeProperties.sink.tableActionOption ??
                    new LakehouseTableCopySink().tableActionOption) as string;
                resource.properties.destination.writeBehavior =
                    convertTableActionOptionToWriteBehavior(tableActionOption);
                Reflect.deleteProperty(resource.properties.destination, 'tableActionOption');

                break;
            case adfModelTypeMap.azureSqlDW.copySinkType:
                const allowCopyCommand = (activity.typeProperties.sink as Model.SqlDWSink).allowCopyCommand;
                writeBehavior = (activity.typeProperties.sink as Model.SqlDWSink).writeBehavior as string;
                if (allowCopyCommand) {
                    resource.properties.destination.writeBehavior = 'Append';
                    Reflect.deleteProperty(resource.properties.destination, 'allowCopyCommand');
                } else if (writeBehavior === 'Upsert') {
                    resource.properties.destination.writeBehavior = 'Upsert';
                } else if (writeBehavior === 'SCD2') {
                    resource.properties.destination.writeBehavior = 'SCD2';
                }
                break;
            case adfModelTypeMap.DataWarehouse.copySinkType:
                writeBehavior = (activity.typeProperties.sink as Model.SqlDWSink).writeBehavior as string;
                if (writeBehavior === copyMethodOptionValues.Upsert) {
                    resource.properties.destination.writeBehavior = 'Upsert';
                } else if (writeBehavior === copyMethodOptionValues.BulkInsert) {
                    resource.properties.destination.writeBehavior = 'Append';
                } else if (writeBehavior === copyMethodOptionValues.SCD2) {
                    resource.properties.destination.writeBehavior = 'SCD2';
                }
                Reflect.deleteProperty(resource.properties.destination, 'allowCopyCommand');
                break;
            case adfModelTypeMap.dynamics.copySinkType:
            case adfModelTypeMap.dynamicsCRM.copySinkType:
            case adfModelTypeMap.commonDataService.copySinkType:
                writeBehavior = (activity.typeProperties.sink as Model.DynamicsSink).writeBehavior;
                if (writeBehavior === writeBehaviorOptionValues.upsert) {
                    resource.properties.destination.writeBehavior = 'Upsert';
                    if (!stringIsNullOrEmpty((activity.typeProperties.sink as Model.DynamicsSink).alternateKeyName)) {
                        resource.properties.destination.upsertSettings = {
                            keys: [(activity.typeProperties.sink as Model.DynamicsSink).alternateKeyName]
                        };
                        Reflect.deleteProperty(resource.properties.destination, 'alternateKeyName');
                    }
                }
                break;
            case adfModelTypeMap.salesforceV2.copySinkType:
            case adfModelTypeMap.salesforceServiceCloudV2.copySinkType:
                writeBehavior = (activity.typeProperties.sink as Model.SalesforceV2Sink).writeBehavior;
                if (writeBehavior === writeBehaviorOptionValues.upsert) {
                    resource.properties.destination.writeBehavior = 'Upsert';
                    if (
                        !stringIsNullOrEmpty(
                            (activity.typeProperties.sink as Model.SalesforceV2Sink).externalIdFieldName
                        )
                    ) {
                        resource.properties.destination.upsertSettings = {
                            keys: [(activity.typeProperties.sink as Model.SalesforceV2Sink).externalIdFieldName]
                        };
                        Reflect.deleteProperty(resource.properties.destination, 'externalIdFieldName');
                    }
                }
                break;
            case adfModelTypeMap.cosmosSql.copySinkType:
            case adfModelTypeMap.cosmosMongo.copySinkType:
            case adfModelTypeMap.mongoDb.copySinkType:
            case adfModelTypeMap.mongoDbAtlas.copySinkType:
            case adfModelTypeMap.oracle.copySinkType:
            case adfModelTypeMap.amazonRedshift.copySinkType:
                writeBehavior = (
                    activity.typeProperties.sink as
                    | Model.CosmosDbSqlApiSink
                    | Model.DocumentDbMongoDbApiCollectionSink
                    | Model.NativeMongoDbSink
                    | Model.MongoDbAtlasSink
                    | Model.OracleSink
                    | Model.AmazonRedshiftSink
                ).writeBehavior;
                switch (writeBehavior) {
                    case writeBehaviorOptionValues.insert:
                        resource.properties.destination.writeBehavior = 'Append';
                        break;
                    case writeBehaviorOptionValues.upsert:
                        resource.properties.destination.writeBehavior = 'Upsert';
                        break;
                    case writeBehaviorOptionValues.SCD2:
                        resource.properties.destination.writeBehavior = 'SCD2';
                        break;
                    default:
                        break;
                }
                break;
            case adfModelTypeMap.azureSearch.copySinkType:
                writeBehavior = (activity.typeProperties.sink as Model.AzureSearchIndexSink).indexAction;
                if (writeBehavior === writeBehaviorOptionValues.merge) {
                    resource.properties.destination.writeBehavior = 'Merge';
                } else if (writeBehavior === writeBehaviorOptionValues.upload) {
                    resource.properties.destination.writeBehavior = 'Replace';
                }
                Reflect.deleteProperty(resource.properties.destination, 'indexAction');
                break;
            case adfModelTypeMap.snowflakeV2.copySinkType:
                writeBehavior = (activity.typeProperties.sink as Model.SnowflakeV2Sink).writeBehavior as string;
                if (writeBehavior === writeBehaviorOptionValues.upsert) {
                    resource.properties.destination.writeBehavior = UpdateMethod.Upsert;
                }
                break;
            case adfModelTypeMap.azurePostgreSQL.copySinkType:
                if (GlobalVariables.cjAzurePGv2) {
                    // AzurePostgreSql sink resource uses `writeMethod` (CopyCommand/BulkInsert/Upsert),
                    // map it to the normalized copy-job `writeBehavior` (Append/Upsert).
                    writeBehavior = (activity.typeProperties.sink as Model.AzurePostgreSQLSink).writeMethod;
                    if (writeBehavior === 'Upsert') {
                        resource.properties.destination.writeBehavior = 'Upsert';
                        const upsertSettings = (activity.typeProperties.sink as Model.AzurePostgreSQLSink)
                            .upsertSettings as Model.WriteBehaviorUpsertOptions;
                        if (isArray(upsertSettings?.keys)) {
                            resource.properties.destination.upsertSettings = { keys: upsertSettings.keys };
                        }
                    } else {
                        resource.properties.destination.writeBehavior = 'Append';
                    }
                    Reflect.deleteProperty(resource.properties.destination, 'writeMethod');
                }
                break;
            case adfModelTypeMap.eventStream.copySinkType:
                resource.properties.destination.writeBehavior = (
                    activity.typeProperties.sink as Model.EventStreamSink
                ).writeBehavior;
                break;
            default:
        }
    }

    private _resolveQueryInToResource(
        activity: Model.CopyJobActivity,
        resource: Model.CopyJobResource['activities'][number],
        datasetConfig: IDatasetTypeMetadata,
        lsConfig?: ILinkedServiceTypeMetadata
    ) {
        const tableAlias = datasetConfig.splitTablePath
            ? datasetConfig.tableAlias || 'table'
            : datasetConfig.tableNameAlias || 'tableName';
        const copySourceType = activity.typeProperties.source.type;
        const queryAlias = lsConfig?.queryAlias || datasetConfig.queryAlias || 'query';
        const hasMultipleQuery = datasetConfig.copyJob?.multipleQuery && activity.typeProperties.source[queryAlias];
        switch (copySourceType) {
            case adfModelTypeMap.azureDataExplorer.copySourceType: {
                const adxSource = activity.typeProperties.source as Model.AzureDataExplorerSource;
                if (adxSource.function != null) {
                    // Copy job nests the invocation under `datasetSettings`; the copy activity keeps it flat on the source.
                    const functionSettings: Model.AzureDataExplorerFunctionSettings = {
                        functionName: adxSource.function,
                        functionParameters: adxSource.functionParameters
                    };
                    resource.properties.source.datasetSettings.functionSettings = functionSettings;
                    Reflect.deleteProperty(resource.properties.source, 'function');
                    Reflect.deleteProperty(resource.properties.source, 'functionParameters');
                    Reflect.deleteProperty(resource.properties.source, 'query');
                    Reflect.deleteProperty(resource.properties.source.datasetSettings, 'table');
                    // the backend rejects function settings combined with a query
                    Reflect.deleteProperty(resource.properties.source.datasetSettings, 'querySettings');
                } else if (
                    (activity.typeProperties.source as Model.AzureDataExplorerSource).query ===
                    activity.typeProperties.source.datasetSettings?.typeProperties?.table
                ) {
                    Reflect.deleteProperty(resource.properties.source, 'query');
                } else if (hasMultipleQuery) {
                    resource.properties.source.datasetSettings.querySettings = {
                        queryName: activity.typeProperties.source.datasetSettings.typeProperties.table,
                        query: activity.typeProperties.source.query
                    };
                    Reflect.deleteProperty(resource.properties.source.datasetSettings, 'table');
                    Reflect.deleteProperty(resource.properties.source, 'query');
                }
                break;
            }
            default:
                if (hasMultipleQuery) {
                    resource.properties.source.datasetSettings.querySettings = {
                        queryName: activity.typeProperties.source.datasetSettings.typeProperties[tableAlias],
                        query: activity.typeProperties.source[queryAlias]
                    };
                    Reflect.deleteProperty(resource.properties.source, queryAlias);
                    Reflect.deleteProperty(resource.properties.source.datasetSettings, tableAlias);
                }
        }
    }

    private _jobQueryToActivityQuery(
        copySourceType: string,
        tableAlias: string,
        queryAlias: string,
        resource: Model.CopyJobActivity
    ) {
        const querySettings = resource.typeProperties.source.datasetSettings?.typeProperties?.querySettings;
        switch (copySourceType) {
            case adfModelTypeMap.azureDataExplorer.copySourceType: {
                const functionSettings: Model.AzureDataExplorerFunctionSettings =
                    resource.typeProperties.source.datasetSettings?.typeProperties?.functionSettings;
                if (functionSettings != null) {
                    const adxSource = resource.typeProperties.source as Model.AzureDataExplorerSource;
                    adxSource.function = functionSettings.functionName;
                    adxSource.functionParameters = functionSettings.functionParameters;
                    Reflect.deleteProperty(
                        resource.typeProperties.source.datasetSettings.typeProperties,
                        'functionSettings'
                    );
                    // Like a query name, the function name stands in for the table as the item's name; saving drops it.
                    resource.typeProperties.source.datasetSettings.typeProperties.table = functionSettings.functionName;
                } else if (querySettings) {
                    resource.typeProperties.source.query = querySettings.query;
                    resource.typeProperties.source.datasetSettings.typeProperties.table = querySettings.queryName;
                } else if (
                    stringIsNullOrEmpty((resource.typeProperties.source as Model.AzureDataExplorerSource).query)
                ) {
                    (resource.typeProperties.source as Model.AzureDataExplorerSource).query =
                        resource.typeProperties.source.datasetSettings?.typeProperties?.table;
                }
                break;
            }
            default:
                if (querySettings) {
                    resource.typeProperties.source[queryAlias] = querySettings.query;
                    resource.typeProperties.source.datasetSettings.typeProperties[tableAlias] = querySettings.queryName;
                }
        }
    }
    /**
     * Helper method to delete properties from an object
     * @param obj The object to delete properties from
     * @param properties Array of property names to delete
     * @private
     */
    private _deleteProperties(obj: Record<string, unknown>, properties: string[]): void {
        for (const prop of properties) {
            Reflect.deleteProperty(obj, prop);
        }
    }

    /**
     * Processes and maps file format-specific settings from copy job resource to ADF
     * For supported dataset types (Avro, ORC, Excel)
     */
    private _processFileFormatSettingsToModel(
        activity: Model.CopyJobResource['activities'][number],
        resource: Model.CopyJobActivity,
        datasetType: string,
        role: 'source' | 'destination'
    ) {
        const activityDatasetSettings = activity.properties[role].datasetSettings;

        const resourceDatasetTypeProperties =
            role === 'source'
                ? resource.typeProperties.source.datasetSettings.typeProperties
                : resource.typeProperties.sink.datasetSettings.typeProperties;

        switch (datasetType) {
            case adfModelTypeMap.avro.datasetType:
                if (!stringIsNullOrEmpty(activityDatasetSettings.compressionCodec)) {
                    resourceDatasetTypeProperties.avroCompressionCodec = activityDatasetSettings.compressionCodec;
                }
                break;
            case adfModelTypeMap.orc.datasetType:
                if (!stringIsNullOrEmpty(activityDatasetSettings.compression?.type)) {
                    resourceDatasetTypeProperties.orcCompressionCodec = activityDatasetSettings.compression.type;
                }
                break;
            case adfModelTypeMap.excel.datasetType:
                if (!stringIsNullOrEmpty(activityDatasetSettings.cellRange)) {
                    resourceDatasetTypeProperties.range = activityDatasetSettings.cellRange;
                }
                break;
            default:
                break;
        }
    }

    /**
     * Processes and maps file format-specific settings from ADF to copy job resource
     * For supported dataset types (Avro, ORC, Excel)
     */
    private _processFileFormatSettingsToResource(
        activity: Model.CopyJobActivity,
        resource: Model.CopyJobResource['activities'][number],
        role: 'source' | 'destination'
    ) {
        const datasetType =
            role === 'source'
                ? activity.typeProperties.source.datasetSettings.type
                : activity.typeProperties.sink.datasetSettings?.type;
        const resourceDatasetSettings = resource.properties[role].datasetSettings;
        const activityDatasetTypeProperties =
            role === 'source'
                ? activity.typeProperties.source.datasetSettings.typeProperties
                : activity.typeProperties.sink.datasetSettings?.typeProperties;
        switch (datasetType) {
            case adfModelTypeMap.avro.datasetType:
                resourceDatasetSettings.compressionCodec = activityDatasetTypeProperties?.avroCompressionCodec;
                this._deleteProperties(resourceDatasetSettings, ['avroCompressionCodec']);
                break;
            case adfModelTypeMap.orc.datasetType:
                resourceDatasetSettings.compression = {
                    type: activityDatasetTypeProperties?.orcCompressionCodec
                };
                this._deleteProperties(resourceDatasetSettings, ['orcCompressionCodec']);
                break;
            case adfModelTypeMap.excel.datasetType:
                resourceDatasetSettings.cellRange = activityDatasetTypeProperties?.range;
                this._deleteProperties(resourceDatasetSettings, ['range']);
                break;
            default:
        }
    }

    private _convertProtocolSettingsToResource(
        activity: Model.CopyJobActivity,
        resource: Model.CopyJobResource['activities'][number]
    ) {
        if (
            [
                adfModelTypeMap.rest.copySourceType,
                adfModelTypeMap.odata.copySourceType,
                adfModelTypeMap.dynamicsAX.copySourceType,
                adfModelTypeMap.sharePointOnlineList.copySourceType
            ].includes(activity.typeProperties.source.type)
        ) {
            const resourceSource = resource.properties.source;
            const activitySource = activity.typeProperties.source as
                | Model.RestSource
                | Model.ODataSource
                | Model.DynamicsAXSource
                | Model.SharePointOnlineListCopySource;

            resourceSource.protocolSettings = {
                requestTimeout: activitySource?.httpRequestTimeout || undefined // string
            };
            this._deleteProperties(resource.properties.source, ['httpRequestTimeout']);

            if (activity.typeProperties.source.type === adfModelTypeMap.rest.copySourceType) {
                // use || for string fallback
                resourceSource.protocolSettings = {
                    ...resourceSource.protocolSettings,
                    requestInterval: activitySource?.requestInterval ?? undefined,
                    requestMethod: activitySource?.requestMethod || undefined,
                    requestBody: activitySource?.requestBody || undefined,
                    additionalHeaders: activitySource?.additionalHeaders ?? undefined,
                    paginationRules: activitySource?.paginationRules ?? undefined
                };
                this._deleteProperties(resource.properties.source, [
                    'requestInterval',
                    'requestMethod',
                    'requestBody',
                    'additionalHeaders',
                    'paginationRules'
                ]);
            }
        }
        if (activity.typeProperties.sink.type === adfModelTypeMap.rest.copySinkType) {
            const resourceSink = resource.properties.destination;
            const activitySink = activity.typeProperties.sink as Model.RestSink;

            const requestInterval =
                typeof activitySink.requestInterval === 'number'
                    ? moment.duration(activitySink.requestInterval, 'ms').format('dd.hh:mm:ss.SSS', {
                        trim: false
                    })
                    : activitySink.requestInterval;

            resourceSink.protocolSettings = {
                requestTimeout: activitySink?.httpRequestTimeout || undefined,
                requestInterval: requestInterval,
                requestMethod: activitySink?.requestMethod || undefined,
                writeBatchSize: activitySink?.writeBatchSize ?? undefined,
                additionalHeaders: activitySink?.additionalHeaders ?? undefined,
                compressionType: activitySink?.httpCompressionType ?? undefined
            };
            this._deleteProperties(resource.properties.destination, [
                'httpRequestTimeout',
                'requestInterval',
                'requestMethod',
                'writeBatchSize',
                'additionalHeaders',
                'httpCompressionType'
            ]);
        }
    }

    /**
     * Remove `type` from `storeSettings` and `formatSettings`, and if they are empty, remove the whole property.
     */
    private _resolveSettingsInToResource(resource: Model.CopyJobResource['activities'][number]) {
        const standardizeSettings = (
            properties:
                | Model.CopyJobResource['activities'][number]['properties']['source']
                | Model.CopyJobResource['activities'][number]['properties']['destination'],
            key: 'storeSettings' | 'formatSettings'
        ) => {
            if (properties[key] != null) {
                Reflect.deleteProperty(properties[key], 'type');

                if (Object.keys(properties[key]).length === 0) {
                    Reflect.deleteProperty(properties, key);
                }
            }
        };

        standardizeSettings(resource.properties.source, 'formatSettings');
        standardizeSettings(resource.properties.source, 'storeSettings');
        standardizeSettings(resource.properties.destination, 'formatSettings');
        standardizeSettings(resource.properties.destination, 'storeSettings');
    }

    private _isVariableLibrary(value: unknown): value is { libraryName: string; variableName: string } {
        return (
            isObject(value) &&
            value !== null &&
            isString((value as { libraryName?: string; variableName?: string }).libraryName) &&
            isString((value as { libraryName?: string; variableName?: string }).variableName)
        );
    }

    private _isExpression(value: unknown): value is { type: string; value: string } {
        return (
            isObject(value) &&
            value !== null &&
            isString((value as { type?: string; value?: string }).type) &&
            isString((value as { type?: string; value?: string }).value)
        );
    }

    private _libraryVariablesPostToResource(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resource: Record<string, any>,
        libraryVariables: Model.CopyJobResource['libraryVariables']
    ) {
        if (resource == null) {
            return;
        }
        const keys = Object.keys(resource);
        for (const key of keys) {
            const value = resource[key];
            if (isLogicAppExpression(value)) {
                const libraryVariablesRef = this._buildLibraryVariables(value, libraryVariables);
                if (libraryVariablesRef) {
                    resource[`${key}VariableLibrary`] = libraryVariablesRef;
                    Reflect.deleteProperty(resource, key);
                }
            } else if (isArray(value)) {
                for (const item of value) {
                    if (isObject(item)) {
                        this._libraryVariablesPostToResource(item, libraryVariables);
                    }
                }
            } else if (isObject(value)) {
                if (this._isExpression(value)) {
                    const libraryVariablesRef = this._buildLibraryVariables(value.value, libraryVariables);
                    if (libraryVariablesRef) {
                        resource[`${key}VariableLibrary`] = libraryVariablesRef;
                        Reflect.deleteProperty(resource, key);
                    }
                } else {
                    this._libraryVariablesPostToResource(value, libraryVariables);
                }
            }
        }
    }

    private _buildLibraryVariables(expression: string, libraryVariables: Model.CopyJobResource['libraryVariables']) {
        if (stringIsNullOrEmpty(expression)) {
            return undefined;
        }
        const match = expression.match(/^@pipeline\(\)\.libraryVariables\.(.+)$/);
        if (!match) {
            return undefined;
        }
        const variableName = match[1];
        if (stringIsNullOrEmpty(variableName)) {
            return undefined;
        }
        const libraryVariable = libraryVariables[variableName];
        if (libraryVariable == null) {
            return undefined;
        }
        return { libraryName: libraryVariable.libraryName, variableName: libraryVariable.variableName };
    }

    private _libraryVariablesPreToModel(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resource: Record<string, any>,
        inlineExpression: boolean,
        root: Model.CopyJobResource
    ) {
        if (resource == null) {
            return;
        }
        const keys = Object.keys(resource);
        for (const key of keys) {
            const value = resource[key];
            if (key.endsWith('VariableLibrary') && this._isVariableLibrary(value)) {
                const libraryVariableName = `libraryVariable_${Math.random().toString(16).slice(2)}`;
                const expression = `@pipeline().libraryVariables.${libraryVariableName}`;
                if (root.libraryVariables == null) {
                    root.libraryVariables = {};
                }
                root.libraryVariables[libraryVariableName] = {
                    libraryName: value.libraryName,
                    variableName: value.variableName
                };
                const newKey = key.substring(0, key.length - 'VariableLibrary'.length);
                resource[newKey] = inlineExpression ? expression : { type: 'Expression', value: expression };
                Reflect.deleteProperty(resource, key);
            } else if (isArray(value)) {
                for (const item of value) {
                    if (isObject(item)) {
                        this._libraryVariablesPreToModel(item, inlineExpression, root);
                    }
                }
            } else if (isObject(value)) {
                this._libraryVariablesPreToModel(value, inlineExpression, root);
            }
        }
    }
}
